import { useEffect, useMemo, useState } from "react";
import { apiPost, apiPatch } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import ModuleChecklist from "./ModuleChecklist";
import { ROLE_LABELS } from "../../lib/modules";

const ROLE_OPTIONS = {
  super_admin: ["super_admin", "org_admin", "analyst", "viewer"],
  org_admin: ["org_admin", "analyst", "viewer"],
};

export default function UserDialog({ open, onOpenChange, user, orgs, actor, onSaved, onCreated }) {
  const editing = !!user;
  const roleChoices = ROLE_OPTIONS[actor.role] || ["viewer"];
  const [form, setForm] = useState({});
  const [restrict, setRestrict] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (user) {
      setForm({
        name: user.name || "", role: user.role, status: user.status || "active",
        module_overrides: user.module_overrides || [],
      });
      setRestrict(user.module_overrides != null);
    } else {
      setForm({
        email: "", name: "", role: roleChoices.includes("viewer") ? "viewer" : roleChoices[0],
        org_id: actor.role === "super_admin" ? (orgs[0]?.id || "") : actor.org_id,
        initial_password: "", module_overrides: [],
      });
      setRestrict(false);
    }
  }, [open, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // modules available for a per-user override = the target org's grants
  const targetOrgId = editing ? user.org_id : form.org_id;
  const availableModules = useMemo(() => {
    const o = orgs.find((x) => x.id === targetOrgId);
    return o ? o.modules : [];
  }, [orgs, targetOrgId]);

  const save = async () => {
    setErr("");
    setBusy(true);
    try {
      if (editing) {
        await apiPatch(`/admin/users/${user.id}`, {
          name: form.name, role: form.role, status: form.status,
          module_overrides: restrict ? form.module_overrides : null,
        });
        onSaved();
        onOpenChange(false);
      } else {
        if (!form.email.trim()) throw { response: { data: { detail: "Email is required." } } };
        const res = await apiPost("/admin/users", {
          email: form.email, name: form.name, role: form.role, org_id: form.org_id,
          initial_password: form.initial_password || undefined,
          module_overrides: restrict ? form.module_overrides : null,
        });
        onSaved();
        onOpenChange(false);
        onCreated(res); // reveal initial password
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="small-caps">{editing ? "Edit user" : "New user"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!editing && (
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleChoices.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && actor.role === "super_admin" && (
              <div className="space-y-1.5">
                <Label>Organization</Label>
                <Select value={form.org_id} onValueChange={(v) => set("org_id", v)}>
                  <SelectTrigger data-testid="user-org"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editing && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {!editing && (
            <div className="space-y-1.5">
              <Label>Initial password <span style={{ color: "var(--cv-dark-grey)" }}>(optional — auto-generated if blank)</span></Label>
              <Input value={form.initial_password} onChange={(e) => set("initial_password", e.target.value)} data-testid="user-initpw" />
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div>
              <Label>Restrict module access</Label>
              <div className="text-[12px]" style={{ color: "var(--cv-dark-grey)" }}>
                Off = inherit all of the org's modules
              </div>
            </div>
            <Switch checked={restrict} onCheckedChange={setRestrict} data-testid="user-restrict" />
          </div>
          {restrict && (
            <ModuleChecklist
              value={form.module_overrides || []}
              onChange={(v) => set("module_overrides", v)}
              available={availableModules}
              testidPrefix="user-mod"
            />
          )}
          {err && <div className="text-[13px]" style={{ color: "var(--cv-crimson)" }} data-testid="user-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="user-save"
            style={{ background: "var(--cv-crimson)", color: "#fff" }}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
